import dayjs from "dayjs";
import * as React from "react";
import { useNavigate } from "react-router";
import type { CellProps, Column as ColumnType, TableState } from "react-table";
import { CLOVIS_STANDART_DATE_FORMAT } from "~/config/dayjs";
import type { CSS } from "~/config/stitches";
import { styled } from "~/config/stitches";
import { notificationsMap } from "~/config/user/user-notifications/notificationsMap";
import type {
  UserNotification,
  UserNotifications,
} from "~/config/user/user-notifications/utils/types";
import { useUser } from "~/config/user/UserProvider";
import { Box } from "~/design-system/Box";
import { Text } from "~/design-system/Text";
import { VirtualTable } from "~/screens/App/components/VirtualTable";
import { useListSelectionKeyboardShortcuts } from "~/screens/App/utils/useListSelectionKeyboardShortcuts/useListSelectionKeyboardShortcuts";
import { createComponentHook } from "~/types";
import { useUserNotificationsStore } from "../../../../../UserNotificationsScreen.store";
import { AvatarFromUserId } from "./components/AvatarFromUserId/AvatarFromUserId";
import { EmptyNotifications } from "./components/EmptyNotifications";

// Approximative height of a row for virtualized list and scrolling
const CELL_HEIGHT = 74;
const PROJECT_SPREADSHEET_IMPORT_REJECTED_NOTIFICATION_TYPE =
  "ORG/PROJECT_SPREADSHEET_IMPORT_REJECTED";

type NotificationsListDesktopProps = {
  notifications?: UserNotifications;
  css?: CSS;
};

const ReadNotifWrapper = styled(Box, {
  variants: {
    isRead: {
      false: {},
      true: { opacity: 0.5 },
    },
  },
});

const useNotificationsListDesktop = createComponentHook(
  (props: NotificationsListDesktopProps) => {
    const { user } = useUser();
    const navigate = useNavigate();
    const { clearSelection, setClearSelection, setSelectedItems } =
      useUserNotificationsStore((state) => ({
        clearSelection: state.clearSelection,
        setClearSelection: state.setClearSelection,
        setSelectedItems: state.setSelectedItems,
      }));

    const [sortedInverse, setSortedInverse] = React.useState(true);

    const { checkboxesRef } = useListSelectionKeyboardShortcuts({
      data: props.notifications ?? [],
      inverse: sortedInverse,
    });

    const columns = React.useMemo<Array<ColumnType<UserNotification>>>(
      () => [
        {
          Cell: (props: CellProps<UserNotification>) => (
            <ReadNotifWrapper isRead={props.cell.row.original.read_at !== null}>
              <AvatarFromUserId userId={props.cell.row.original.creator_id} />
            </ReadNotifWrapper>
          ),
          accessor: (row) => row.payload.creator.firstName,
          align: "center",
          basis: 0.5,
          id: "author",
        },
        {
          Cell: (props: CellProps<UserNotification>) => {
            const shouldWrapContent =
              props.cell.row.original.type ===
              PROJECT_SPREADSHEET_IMPORT_REJECTED_NOTIFICATION_TYPE;

            return (
              <ReadNotifWrapper
                isRead={props.cell.row.original.read_at !== null}
                css={
                  shouldWrapContent
                    ? {
                        display: "block",
                        lineHeight: "1.25rem",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                      }
                    : undefined
                }
              >
                {
                  notificationsMap[props.cell.row.original.type](
                    { user },
                    props.cell.row.original as never
                  ).content
                }
              </ReadNotifWrapper>
            );
          },
          accessor: (row: UserNotification) =>
            notificationsMap[row.type]({ user }, row as never).content,
          id: "description",
        },
        {
          Cell: (props: CellProps<UserNotification>) => (
            <ReadNotifWrapper isRead={props.cell.row.original.read_at !== null}>
              <Text size="small">
                {dayjs(props.cell.row.original.created_at).format(
                  CLOVIS_STANDART_DATE_FORMAT
                )}
              </Text>
            </ReadNotifWrapper>
          ),
          accessor: (row) => row.created_at,
          basis: 0.7,
          id: "date",
        },
      ],
      [user]
    );

    const initialState: Partial<TableState<UserNotification>> = {
      sortBy: [
        {
          desc: true,
          id: "date",
        },
      ],
    };

    const rowClickHandler = (row: UserNotification) => {
      const linkToRedirect = notificationsMap[row.type](
        { user },
        row as never
      ).link;
      navigate(linkToRedirect);
    };

    return {
      actions: {
        rowClickHandler,
        setClearSelection,
        setSelectedItems,
        setSortedInverse,
      },
      state: {
        checkboxesRef,
        clearSelection,
        columns,
        initialState,
      },
    };
  }
);

const NotificationsListDesktop = (props: NotificationsListDesktopProps) => {
  const { actions, state } = useNotificationsListDesktop(props);

  if (!props.notifications || props.notifications.length === 0) {
    return <EmptyNotifications />;
  }

  return (
    <>
      {state.checkboxesRef && (
        <VirtualTable
          setSelectedItems={actions.setSelectedItems}
          clearSelection={state.clearSelection}
          setClearSelection={actions.setClearSelection}
          canSelectItems
          css={props.css}
          data={props.notifications}
          columns={state.columns}
          estimateSize={() => CELL_HEIGHT}
          initialState={state.initialState}
          rowProps={{
            onClick: actions.rowClickHandler,
          }}
          ref={state.checkboxesRef}
          setSortedDirection={actions.setSortedInverse}
        />
      )}
    </>
  );
};

export { NotificationsListDesktop, ReadNotifWrapper };
